import type { Option } from '@byecode/ui'
import { Popover, SelectDropdown, Text } from '@byecode/ui'
import type { WxMessageInfoRes } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { EmptyMiniProgramTemplate } from '../component/EmptyMiniProgramTemplate'
import * as SC from './styles'

interface TemplateItemProps {
    disabled?: boolean
    data: Option | undefined
    onClose: () => void
}

const TemplateItem: React.FC<TemplateItemProps> = ({ disabled, data, onClose }) => {
    return (
        <SC.ListItem gap={8} justifyContent="space-between" alignItems="center">
            {data ? <SC.Tag>{data.label}</SC.Tag> : <SC.ErrorTag>找不到消息模板</SC.ErrorTag>}
            {!disabled && <SC.Close type="Close" onClick={onClose} />}
        </SC.ListItem>
    )
}

interface SelectTemplateMsgProps {
    value: string[] | undefined
    data?: WxMessageInfoRes
    onChange: (val: string[]) => void
}

export const SelectTemplateMsg: React.FC<SelectTemplateMsgProps> = ({ value, data, onChange }) => {
    const [open, setOpen] = useState(false)

    const disabledWithVersion = useIsDisabledWithVersion()

    const allOptions = useMemo(() => {
        if (!data) {
            return []
        }
        return data.map(item => ({
            label: item.title,
            value: item.templateId
        }))
    }, [data])

    const options = useMemo(() => allOptions.filter(item => !value?.includes(item.value)), [allOptions, value])

    const handleChange = useCallback(
        (val: string) => {
            setOpen(false)
            onChange([...(value || []), val])
        },
        [onChange, value]
    )

    const handleDelete = useCallback(
        (val: string) => {
            if (!value) {
                return
            }
            const newValue = value.filter(id => id !== val)
            onChange(newValue)
        },
        [onChange, value]
    )

    const isLimit = useMemo(() => value && value.length > 2, [value])

    return (
        <SC.Container>
            <SC.List>
                {value?.map(id => {
                    const data = find(item => item.value === id, allOptions)
                    return <TemplateItem disabled={disabledWithVersion} key={id} data={data} onClose={() => handleDelete(id)} />
                })}
            </SC.List>
            <Popover position="bottom-start" disabled={isLimit} width={328} withinPortal opened={open} onChange={setOpen}>
                <Popover.Target>
                    <SC.ButtonContainer>
                        <SC.ButtonContent
                            disabled={isLimit}
                            radius={100}
                            icon={<SC.Icon type="Add" fill="var(--color-gray-500)" size={16} />}
                        >
                            <Text>添加</Text>
                            <Text>（{value?.length ?? 0}/3）</Text>
                        </SC.ButtonContent>
                    </SC.ButtonContainer>
                </Popover.Target>
                <Popover.Dropdown>
                    <SelectDropdown searchable options={options} emptyComponent={<EmptyMiniProgramTemplate />} onSelect={handleChange} />
                </Popover.Dropdown>
            </Popover>
        </SC.Container>
    )
}
